import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Languages } from 'lucide-react';

// ---------------------------------------------------------------
// TranslateButton
// ---------------------------------------------------------------
// Drops Google Translate's official widget into the page (free,
// no API key, works on every text node including dynamic React
// content) and exposes a single branded toggle button that flips
// the page between English and Spanish.
//
// Implementation notes
// ---------------------------------------------------------------
//  * The Google Translate engine reads a `googtrans` cookie to
//    decide the target language. We set it to `/en/es` for Spanish
//    and clear it for English. Reloading once after the cookie
//    change lets the engine sweep the entire page (including any
//    dialogs / table rows / toasts loaded later).
//  * The widget itself renders a hidden `<div id="…element">` —
//    we keep it offscreen so only our styled button is visible.
//  * Preference persists in localStorage under `lfd_lang` so the
//    next page-load applies the same language without flicker.
//
// Usage:
//    <TranslateButton />
//    // anywhere on the dashboard; mount once is enough for the
//    // whole admin panel because the widget scope is the document.

const STORAGE_KEY = 'lfd_lang';
const COOKIE_KEY = 'googtrans';
const WIDGET_ELEMENT_ID = 'lfd-google-translate-widget';
const WIDGET_SCRIPT_ID = 'lfd-gt-script';

const _setLangCookie = (lang) => {
  // Google reads the cookie at multiple scopes — set both the
  // exact host AND the apex domain so the cookie survives a
  // www. ↔ apex hop on production.
  const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
  const cookieVal = lang === 'es' ? '/en/es' : '';
  const apex = window.location.hostname.split('.').slice(-2).join('.');
  // Host-only
  document.cookie = `${COOKIE_KEY}=${cookieVal}; path=/; expires=${expires}`;
  // Apex domain (so .lfdportal.com cookie covers www.lfdportal.com)
  if (apex && apex.includes('.')) {
    document.cookie = `${COOKIE_KEY}=${cookieVal}; path=/; expires=${expires}; domain=.${apex}`;
  }
  if (lang !== 'es') {
    // Force-clear the cookie on EN switch
    document.cookie = `${COOKIE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    if (apex && apex.includes('.')) {
      document.cookie = `${COOKIE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${apex}`;
    }
  }
};

const TranslateButton = ({ variant = 'default', className = '' }) => {
  const [lang, setLang] = useState(() => localStorage.getItem(STORAGE_KEY) || 'en');

  // Inject the Google Translate widget script once per app lifetime.
  useEffect(() => {
    if (document.getElementById(WIDGET_SCRIPT_ID)) return;
    if (window.googleTranslateElementInit) return;

    // The widget's loader looks for this global callback function.
    window.googleTranslateElementInit = () => {
      try {
        // eslint-disable-next-line no-new
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,es',
            autoDisplay: false,
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          },
          WIDGET_ELEMENT_ID,
        );
      } catch (err) { /* widget already mounted — safe to ignore */ }
    };

    const script = document.createElement('script');
    script.id = WIDGET_SCRIPT_ID;
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);

    // Inject CSS to silence Google's default "powered by" banner +
    // hide the auto-injected top frame ("Original text" tooltip)
    // that otherwise pushes the entire page down.
    if (!document.getElementById('lfd-gt-style')) {
      const style = document.createElement('style');
      style.id = 'lfd-gt-style';
      style.innerHTML = `
        /* hide the floating banner Google adds to translated pages */
        .goog-te-banner-frame.skiptranslate { display: none !important; }
        body { top: 0 !important; }
        /* hide the widget itself — we replace it with our own button */
        #${WIDGET_ELEMENT_ID} { display: none !important; }
        /* hide the small "Original text" popup on hover */
        .goog-tooltip, .goog-tooltip:hover, .goog-text-highlight {
          display: none !important;
          background-color: transparent !important;
          box-shadow: none !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleToggle = () => {
    const next = lang === 'en' ? 'es' : 'en';
    setLang(next);
    localStorage.setItem(STORAGE_KEY, next);
    _setLangCookie(next);
    // A full reload is the most reliable way to (a) apply the new
    // cookie to the Google widget and (b) restore native English text
    // when switching back. The page restore is well within the user's
    // expectation since they pressed a deliberate toggle.
    window.location.reload();
  };

  const isSpanish = lang === 'es';
  return (
    <>
      {/* The hidden Google Translate widget element. Required for the
          translation engine to attach to the DOM, but visually hidden
          via the CSS above. */}
      <div id={WIDGET_ELEMENT_ID} aria-hidden="true" />

      <Button
        type="button"
        variant={variant}
        onClick={handleToggle}
        className={`gap-2 ${
          isSpanish
            ? 'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-600'
            : 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-300'
        } notranslate ${className}`}
        title={isSpanish ? 'Switch back to English' : 'Translate the entire admin panel to Spanish'}
        data-testid="translate-toggle-btn"
      >
        <Languages size={16} />
        <span className="notranslate">{isSpanish ? 'EN' : 'ES'}</span>
        <span className="text-xs opacity-80 hidden sm:inline notranslate">
          {isSpanish ? '· Back to English' : '· Traducir al español'}
        </span>
      </Button>
    </>
  );
};

export default TranslateButton;
